'use client';

import React from 'react';
import Link from 'next/link';
import Header from '@/components/common/Header';
import Footer from '@/app/homepage/components/SiteFooter';

export default function CookiePolicyPage() {
  const sections = [
    {
      id: 'what-are-cookies',
      title: 'What Are Cookies?',
      body: (
        <>
          <p className="mb-4">
            Cookies are small text files that are placed on your device when you visit a website. They are widely
            used to make websites work, or work more efficiently, as well as to provide information to the site
            owners.
          </p>
          <p>
            Shashio uses cookies and similar technologies (such as local storage and pixels) to keep you signed in,
            remember your preferences, and understand how our platform is used so we can improve it.
          </p>
        </>
      ),
    },
    {
      id: 'types',
      title: 'Types of Cookies We Use',
      body: (
        <>
          <p className="mb-4">We use the following categories of cookies:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li><strong className="text-white">Essential cookies:</strong> Required for the platform to function — for example, keeping you logged in and securing your session.</li>
            <li><strong className="text-white">Preference cookies:</strong> Remember your settings and choices, such as language and display options.</li>
            <li><strong className="text-white">Analytics cookies:</strong> Help us understand how visitors interact with our site so we can improve performance and usability.</li>
            <li><strong className="text-white">Marketing cookies:</strong> Used to measure the effectiveness of campaigns and, where applicable, deliver relevant content.</li>
          </ul>
        </>
      ),
    },
    {
      id: 'why-we-use',
      title: 'Why We Use Cookies',
      body: (
        <>
          <p className="mb-4">Cookies allow us to:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li>Authenticate you and keep your account secure</li>
            <li>Remember your dashboard preferences and order settings</li>
            <li>Measure and improve the performance of our editing platform</li>
            <li>Understand which features and pages are most useful to our clients</li>
          </ul>
        </>
      ),
    },
    {
      id: 'third-party',
      title: 'Third-Party Cookies',
      body: (
        <>
          <p className="mb-4">
            Some cookies are placed by third-party services that appear on our pages. We work only with reputable
            providers who help us operate and improve our service, such as:
          </p>
          <ul className="list-disc pl-6 space-y-2">
            <li>Analytics providers to understand usage patterns</li>
            <li>Payment processors to securely handle transactions</li>
            <li>Content delivery networks to serve images and assets quickly</li>
          </ul>
        </>
      ),
    },
    {
      id: 'managing',
      title: 'Managing Your Cookies',
      body: (
        <>
          <p className="mb-4">
            You can control and manage cookies in several ways. Most browsers let you view, delete, and block
            cookies through their settings. Please note that removing or blocking cookies may impact your experience
            and some parts of the platform may no longer work as intended.
          </p>
          <p>
            To manage cookies, check your browser&apos;s help section — most major browsers (Chrome, Safari, Firefox,
            Edge) provide clear instructions for adjusting cookie preferences.
          </p>
        </>
      ),
    },
    {
      id: 'updates',
      title: 'Updates to This Policy',
      body: (
        <>
          <p className="mb-4">
            We may update this Cookie Policy from time to time to reflect changes in technology, legislation, or our
            practices. Any changes will be posted on this page with an updated revision date.
          </p>
          <p>We encourage you to review this policy periodically to stay informed about how we use cookies.</p>
        </>
      ),
    },
    {
      id: 'contact',
      title: 'Contact Us',
      body: (
        <>
          <p className="mb-4">If you have any questions about our use of cookies, please contact us:</p>
          <div className="bg-white/[0.03] border border-white/[0.08] rounded-xl p-6 space-y-2">
            <p><strong className="text-white">Email:</strong> <a href="mailto:privacy@shashio.com" className="text-purple-400 hover:text-purple-300 transition-colors">privacy@shashio.com</a></p>
            <p><strong className="text-white">Website:</strong> www.shashio.com</p>
            <p><strong className="text-white">Address:</strong> Shashio Privacy Department</p>
          </div>
        </>
      ),
    },
  ];

  const dotGrid = `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`;

  return (
    <div className="min-h-screen bg-[#0B0D17] text-white">
      <Header />

      {/* ===================== HERO ===================== */}
      <section className="relative overflow-hidden bg-[#0B0D17]">
        <div className="absolute inset-0">
          <div className="absolute top-0 left-1/3 w-[1000px] h-[1000px] bg-purple-600/10 rounded-full blur-[160px] animate-glow-breathe" />
          <div className="absolute bottom-0 right-1/3 w-[800px] h-[800px] bg-blue-600/5 rounded-full blur-[140px] animate-glow-breathe-delayed" />
          <div className="absolute inset-0 opacity-[0.025]" style={{ backgroundImage: dotGrid }} />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 py-24 lg:py-28 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/[0.04] border border-white/[0.12] backdrop-blur-sm text-purple-200 text-xs font-medium mb-8">
            <span className="text-sm">🍪</span>
            <span>How We Use Cookies</span>
          </div>
          <h1 className="text-5xl lg:text-7xl font-bold leading-[1.05] tracking-tight mb-5">
            Cookie{' '}
            <span className="bg-gradient-to-r from-purple-400 via-purple-300 to-blue-400 bg-clip-text text-transparent">
              Policy.
            </span>
          </h1>
          <p className="text-lg text-gray-400">Last updated: January 1, 2026</p>
        </div>
      </section>

      {/* ===================== CONTENT ===================== */}
      <section className="relative py-16 lg:py-24 px-6 lg:px-12 border-t border-white/[0.06]">
        <div className="max-w-6xl mx-auto grid lg:grid-cols-[260px_1fr] gap-10 xl:gap-16">
          {/* Table of contents */}
          <aside className="hidden lg:block">
            <div className="sticky top-24">
              <p className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-4">On this page</p>
              <nav className="space-y-1">
                {sections.map((s, i) => (
                  <a
                    key={s.id}
                    href={`#${s.id}`}
                    className="block px-3 py-2 rounded-lg text-sm text-gray-400 hover:text-white hover:bg-white/[0.04] transition-all duration-200"
                  >
                    <span className="text-purple-400 font-semibold mr-2">{i + 1}.</span>
                    {s.title}
                  </a>
                ))}
              </nav>
            </div>
          </aside>

          {/* Sections */}
          <div className="space-y-6">
            <div className="bg-[#13151F]/60 border border-white/[0.08] rounded-2xl p-6 lg:p-8 text-gray-400 leading-relaxed">
              This Cookie Policy explains how Shashio uses cookies and similar technologies when you use our real
              estate photo editing platform, and the choices you have.
            </div>

            {sections.map((section, i) => (
              <div
                key={section.id}
                id={section.id}
                className="scroll-mt-24 bg-[#13151F]/80 backdrop-blur-sm border border-white/[0.08] rounded-2xl p-6 lg:p-8 hover:border-purple-500/20 transition-all duration-300"
              >
                <div className="flex items-center gap-4 mb-5">
                  <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center text-white font-bold shadow-lg shadow-purple-500/30">
                    {i + 1}
                  </div>
                  <h2 className="text-xl lg:text-2xl font-bold text-white">{section.title}</h2>
                </div>
                <div className="text-gray-400 leading-relaxed">{section.body}</div>
              </div>
            ))}

            {/* Footer CTA */}
            <div className="relative overflow-hidden rounded-2xl border-2 border-white/[0.12] bg-[#13151F]/95 backdrop-blur-2xl p-8 lg:p-10 text-center">
              <div className="absolute -top-16 left-1/2 -translate-x-1/2 w-[500px] h-[300px] bg-purple-600/15 rounded-full blur-[120px]" />
              <div className="relative z-10">
                <h3 className="text-2xl font-bold text-white mb-3">Questions about cookies?</h3>
                <p className="text-gray-400 mb-6 max-w-xl mx-auto">
                  We&apos;re happy to explain exactly what we store and why.
                </p>
                <Link href="/contact">
                  <button className="px-8 py-3.5 bg-gradient-to-r from-purple-600 to-purple-500 hover:from-purple-500 hover:to-purple-400 text-white font-bold rounded-xl transition-all duration-300 shadow-lg shadow-purple-500/30 hover:-translate-y-0.5">
                    Contact Us
                  </button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />

      {/* Animations */}
      <style jsx global>{`
        @keyframes glow-breathe {
          0%, 100% { opacity: 0.1; transform: scale(1); }
          50% { opacity: 0.15; transform: scale(1.05); }
        }
        @keyframes glow-breathe-delayed {
          0%, 100% { opacity: 0.05; transform: scale(1); }
          50% { opacity: 0.08; transform: scale(1.03); }
        }
        .animate-glow-breathe { animation: glow-breathe 8s ease-in-out infinite; }
        .animate-glow-breathe-delayed { animation: glow-breathe-delayed 10s ease-in-out infinite; animation-delay: 2s; }
      `}</style>
    </div>
  );
}
